// src/poker/table/PlayersPanel.js
import React from "react";
import Card from "./Card";
import { translateDescrPL, formatAmount, formatAction } from "./helpers";
import { ORANGE } from "./labels";

function fmtSignedAmount(value, stackViewMode, bbAmount) {
  const x = Number(value || 0);
  if (!x) return null;
  const sign = x > 0 ? "+" : "−";
  const abs = Math.abs(x);
  const absTxt = formatAmount(abs, stackViewMode, bbAmount);
  return `${sign}${absTxt}`;
}

function resultLabel(value) {
  const x = Number(value || 0);
  if (!x) return null;
  return x > 0 ? "ZYSK" : "STRATA";
}

function seatDisplayName(seatObj) {
  return (
    seatObj?.nickname ||
    seatObj?.nick ||
    seatObj?.name ||
    seatObj?.displayName ||
    seatObj?.email ||
    seatObj?.userEmail ||
    seatObj?.user?.email ||
    "Gracz"
  );
}

// ✅ Motyw: próbujemy jak reszta UI (data-theme / klasy), fallback na prefers-color-scheme
function isDarkThemeNow() {
  if (typeof document === "undefined") return true;

  const el = document.documentElement;
  const body = document.body;

  const dt = (el?.getAttribute?.("data-theme") || body?.getAttribute?.("data-theme") || "").toLowerCase();
  if (dt === "dark") return true;
  if (dt === "light") return false;

  const cls = `${el?.className || ""} ${body?.className || ""}`.toLowerCase();
  if (cls.includes("dark")) return true;
  if (cls.includes("light")) return false;

  if (window?.matchMedia?.("(prefers-color-scheme: dark)")?.matches) return true;
  return false;
}

// ✅ Spróbuj wywnioskować blind z seatState (jeśli już tam jest)
function inferBlindRole(st) {
  const raw =
    st?.blind ??
    st?.blindRole ??
    st?.postedBlind ??
    st?.blindType ??
    st?.positionRole ??
    "";

  const s = String(raw).toLowerCase();

  const isSB =
    s === "sb" ||
    s === "small" ||
    s === "smallblind" ||
    s === "small_blind" ||
    s === "small-blind" ||
    s.includes("small");

  const isBB =
    s === "bb" ||
    s === "big" ||
    s === "bigblind" ||
    s === "big_blind" ||
    s === "big-blind" ||
    s.includes("big");

  return { isSB, isBB };
}

export default function PlayersPanel({
  seats,
  seatState,
  mySeatNo,
  actionSeat,
  dealerSeat,

  // ✅ NOWE (opcjonalne): jeśli przekażesz z game state, masz 100% poprawności
  smallBlindSeat,
  bigBlindSeat,
  sbSeat,
  bbSeat,

  showHandResult,
  bySeat,
  winners,
  winnersSet,
  payouts,
  revealAll, // kompatybilność
  street, // "preflop"|"flop"|"turn"|"river"|"showdown"|"complete"
  showdownHoles,
  onShowCards,
  lastActionBySeat,
  handId,

  stackViewMode = "chips",
  bbAmount = 0,
}) {
  // ✅ dynamiczny kolor obrysu dla TURA (jak było)
  const isDarkTheme = isDarkThemeNow();
  const actionBorderColor = isDarkTheme ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.72)";
  const actionGlowColor = isDarkTheme ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)";
  const actionInsetColor = isDarkTheme ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.18)";

  const borderFor = ({ isWinner, isAction, isAllIn, isOut, isFolded, isMeSeat }) => {
    // ✅ winner highlight (end of hand)
    if (showHandResult && isWinner) return "2px solid rgba(80,220,140,0.90)";
    if (isOut) return "2px solid rgba(255,80,80,0.85)";
    if (isFolded) return "1px solid rgba(255,255,255,0.12)";
    if (isAction) return `2px solid ${actionBorderColor}`;
    if (isAllIn) return "2px solid rgba(255,165,0,0.95)";
    if (isMeSeat) return "1px solid rgba(255,165,0,0.65)";
    return "1px solid rgba(255,255,255,0.15)";
  };

  const glowFor = ({ isWinner, isAction, isAllIn, isOut, isMeSeat }) => {
    // ✅ winner highlight (end of hand)
    if (showHandResult && isWinner) return "0 0 0 1px rgba(80,220,140,0.22) inset, 0 0 22px rgba(80,220,140,0.18)";
    if (isOut) return "0 0 0 1px rgba(255,80,80,0.18) inset, 0 0 18px rgba(255,80,80,0.12)";
    if (isAction) return `0 0 0 1px ${actionInsetColor} inset, 0 0 22px ${actionGlowColor}`;
    if (isAllIn) return "0 0 0 1px rgba(255,165,0,0.16) inset, 0 0 18px rgba(255,165,0,0.12)";
    if (isMeSeat) return "0 0 0 1px rgba(255,165,0,0.10) inset";
    return "0 0 0 1px rgba(255,255,255,0.06) inset";
  };

  const canRevealPhase = street === "showdown" || street === "complete";
  const handDealt = Number(handId || 0) > 0;

  const flipMs = 900;
  const flipGapMs = 160;

  const dealMs = 900;
  const dealGapMs = 160;

  const renderEmptyCardRect = (key) => (
    <div key={key} className="poker-card-rect poker-card-rect--xs poker-card-rect--empty" />
  );

  const renderHoleCards = ({ seatNoStr, st, isOut, isLeft }) => {
    const inHand = !!st?.inHand;
    if (!inHand || isOut || isLeft) return null;

    const publicCards = showdownHoles?.[seatNoStr] || null;
    const hasPublic = Array.isArray(publicCards) && publicCards.length === 2;

    if (canRevealPhase && hasPublic) {
      return (
        <div className="hole-cards" style={{ marginTop: 0 }}>
          <Card
            key={`ph-${seatNoStr}-0-${handId || 0}-${publicCards[0]?.r || "X"}${publicCards[0]?.s || "X"}`}
            c={publicCards[0]}
            size="xs"
            revealMode="flip"
            revealMs={flipMs}
            revealDelayMs={0}
          />
          <Card
            key={`ph-${seatNoStr}-1-${handId || 0}-${publicCards[1]?.r || "X"}${publicCards[1]?.s || "X"}`}
            c={publicCards[1]}
            size="xs"
            revealMode="flip"
            revealMs={flipMs}
            revealDelayMs={flipGapMs}
          />
        </div>
      );
    }

    if (handDealt) {
      return (
        <div className="hole-cards" style={{ marginTop: 0 }}>
          <Card
            key={`dd-${seatNoStr}-0-${handId || 0}`}
            c={{}}
            faceDown
            size="xs"
            revealMode="wipeUp"
            revealMs={dealMs}
            revealDelayMs={0}
          />
          <Card
            key={`dd-${seatNoStr}-1-${handId || 0}`}
            c={{}}
            faceDown
            size="xs"
            revealMode="wipeUp"
            revealMs={dealMs}
            revealDelayMs={dealGapMs}
          />
        </div>
      );
    }

    return (
      <div className="hole-cards" style={{ marginTop: 0 }}>
        {renderEmptyCardRect(`e-${seatNoStr}-0`)}
        {renderEmptyCardRect(`e-${seatNoStr}-1`)}
      </div>
    );
  };

  // ✅ wspólny styl “żetonów”
  const chipStyle = {
    width: 22,
    height: 22,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 11,
    background: "rgba(255,165,0,0.25)",
    border: "1px solid rgba(255,165,0,0.55)",
    color: "rgba(255,165,0,0.95)",
    letterSpacing: 0.2,
  };

  // ✅ seatNo dla SB/BB jeśli parent poda
  const sbSeatResolved = sbSeat ?? smallBlindSeat;
  const bbSeatResolved = bbSeat ?? bigBlindSeat;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 900, marginBottom: 8 }}>Gracze</div>

      <div style={{ display: "grid", gap: 10 }}>
        {seats.map((s) => {
          const seatNoStr = String(s.seat);
          const st = seatState?.[seatNoStr] || {};

          const isAction = actionSeat != null && Number(actionSeat) === Number(s.seat);
          const isMeSeat = mySeatNo != null && Number(mySeatNo) === Number(s.seat);
          const isDealerSeat = dealerSeat != null && Number(dealerSeat) === Number(s.seat);

          const isOffline = st.connected === false;
          const isSittingOut = !!st.sittingOut;
          const isLeft = !!st.left;

          const isFolded = !!st.folded;
          const isAllIn = !!st.inHand && !st.folded && Number(st.stack || 0) === 0;
          const isOut = (!st.inHand && Number(st.stack || 0) === 0) || isLeft;

          const { isSB: inferredSB, isBB: inferredBB } = inferBlindRole(st);

          const isSmallBlindSeat =
            sbSeatResolved != null ? Number(sbSeatResolved) === Number(s.seat) : inferredSB;

          const isBigBlindSeat =
            bbSeatResolved != null ? Number(bbSeatResolved) === Number(s.seat) : inferredBB;

          const h = bySeat?.[seatNoStr];
          const win = winnersSet?.has?.(Number(s.seat));
          const pay = Number(payouts?.[seatNoStr] || 0);

          const deltaTxt = showHandResult ? fmtSignedAmount(pay, stackViewMode, bbAmount) : null;
          const lab = showHandResult ? resultLabel(pay) : null;

          const border = borderFor({ isWinner: win, isAction, isAllIn, isOut, isFolded, isMeSeat });
          const boxShadow = glowFor({ isWinner: win, isAction, isAllIn, isOut, isMeSeat });

          const opacity = isOut ? 0.6 : isFolded ? 0.75 : st.inHand ? 1 : 0.85;

          const hasShowdownCards = !!showdownHoles?.[seatNoStr];
          const showShowCardsButton =
            canRevealPhase && isMeSeat && !hasShowdownCards && !isOut && !isLeft && typeof onShowCards === "function";

          const last = lastActionBySeat?.[seatNoStr] || null;
          const lastText = formatAction(last, stackViewMode, bbAmount);
          const showLastAction = !isOut && !isFolded && !!lastText;

          return (
            <div
              key={s.seat}
              className="panel-card"
              style={{
                padding: 10,
                border,
                borderRadius: 14,
                boxShadow,
                opacity,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 14,
                  alignItems: "start",
                }}
              >
                <div style={{ paddingTop: 0 }}>{renderHoleCards({ seatNoStr, st, isOut, isLeft })}</div>

                <div style={{ textAlign: "right", minWidth: 170 }}>
                  {/* badge row: (Ty) + D + SB + BB + reszta */}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 4 }}>
                    {isMeSeat ? <span style={{ fontWeight: 900, fontSize: 12 }}>(Ty)</span> : null}

                    {isDealerSeat ? (
                      <span title="Dealer" style={chipStyle}>
                        D
                      </span>
                    ) : null}

                    {isSmallBlindSeat ? (
                      <span title="Small Blind" style={chipStyle}>
                        SB
                      </span>
                    ) : null}

                    {isBigBlindSeat ? (
                      <span title="Big Blind" style={chipStyle}>
                        BB
                      </span>
                    ) : null}

                    {isAction ? <span style={{ fontWeight: 900, fontSize: 12 }}>TURA</span> : null}

                    {isAllIn ? (
                      <span style={{ fontWeight: 900, fontSize: 12, color: "rgba(255,165,0,0.95)" }}>ALL-IN</span>
                    ) : null}

                    {isFolded && !isOut ? (
                      <span style={{ fontWeight: 900, fontSize: 12, opacity: 0.75 }}>FOLD</span>
                    ) : null}

                    {isOut ? (
                      <span style={{ fontWeight: 900, fontSize: 12, color: "rgba(255,80,80,0.95)" }}>OUT</span>
                    ) : null}

                    {isOffline ? (
                      <span style={{ fontWeight: 900, fontSize: 12, opacity: 0.75 }}>OFFLINE</span>
                    ) : null}

                    {isSittingOut && !isLeft ? (
                      <span style={{ fontWeight: 900, fontSize: 12, color: "rgba(255,165,0,0.85)" }}>SIT OUT</span>
                    ) : null}

                    {isLeft ? (
                      <span style={{ fontWeight: 900, fontSize: 12, color: "rgba(255,80,80,0.95)" }}>LEFT</span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 13,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={seatDisplayName(s)}
                  >
                    {seatDisplayName(s)} ({s.seat})
                  </div>

                  <div style={{ marginTop: 4, fontSize: 13, whiteSpace: "nowrap", opacity: 0.92 }}>
                    stack: <b>{formatAmount(st.stack, stackViewMode, bbAmount)}</b>
                  </div>

                  <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9, whiteSpace: "nowrap" }}>
                    ostatnio:{" "}
                    {showLastAction ? (
                      <b style={{ color: ORANGE }}>{lastText}</b>
                    ) : (
                      <span style={{ opacity: 0.35 }}>—</span>
                    )}
                  </div>

                  <div style={{ marginTop: 2, fontSize: 12, opacity: 0.92, whiteSpace: "nowrap" }}>
                    wkład:{" "}
                    <b style={{ color: ORANGE }}>
                      {formatAmount(Number(st.committedStreet || 0), stackViewMode, bbAmount)}
                    </b>
                  </div>

                  {showHandResult && deltaTxt ? (
                    <div style={{ marginTop: 2, fontSize: 12, fontWeight: 900, color: ORANGE, whiteSpace: "nowrap" }}>
                      {lab ? `${lab} ` : ""}
                      {deltaTxt}
                    </div>
                  ) : null}

                  {showHandResult && win ? (
                    <div style={{ marginTop: 4, fontWeight: 900, fontSize: 12 }}>
                      {winners?.length > 1 ? "✅ REMIS" : "✅ WYGRANA"}
                    </div>
                  ) : null}
                </div>
              </div>

              {showHandResult && h ? (
                <div style={{ marginTop: 6, opacity: 0.95, fontSize: 13 }}>
                  Układ: <b>{translateDescrPL(h.descr || h.name)}</b>
                </div>
              ) : null}

              {showShowCardsButton ? (
                <div style={{ marginTop: 8 }}>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => onShowCards?.()}
                    title="Pokaż obie karty (dla wszystkich)."
                  >
                    Pokaż karty
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}